/* bs-product-related — carrusel mobile + add-to-cart de las cards, PDP Bienestar Senior.
   El contenido de <product-recommendations class="bs-product-related-root"> se inyecta ASYNC:
   product-recommendations.js (genérico, Horizon) hace fetch on-intersection y sustituye
   this.innerHTML por el HTML real. Por eso:
     - Los listeners de click (nav prev/next + add-to-cart) van DELEGADOS en el propio root
       (que NO se destruye en el swap, solo su contenido) — sobreviven al innerHTML swap sin
       reinit.
     - El listener de 'scroll' del track SÍ necesita re-atarse tras cada swap, porque 'scroll'
       no burbujea desde un elemento hijo hacia el root (spec DOM) y el nodo <track> viejo se
       destruye. Para eso usamos un MutationObserver sobre el root (childList+subtree) que
       reengancha el listener de scroll y recalcula el contador/estado disabled cada vez que
       aparece un __track nuevo (guardado con un data-attr por nodo, R45).
   IIFE (R56), querySelectorAll para multi-instancia (R44), shopify:section:load (R48). */
(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ============================================================
     CARRUSEL — track scroll + nav prev/next + contador
     ============================================================ */

  function getCards(track) {
    return track.querySelectorAll('.bs-product-related, .bs-product-related-root__skeleton-item');
  }

  function getStep(track) {
    var cards = getCards(track);
    if (!cards.length) return 0;
    var style = window.getComputedStyle(track);
    var gap = parseFloat(style.columnGap || style.gap) || 0;
    return cards[0].offsetWidth + gap;
  }

  function refreshNavUi(root, track) {
    var prevBtn = root.querySelector('[data-direction="prev"]');
    var nextBtn = root.querySelector('[data-direction="next"]');
    var counter = root.querySelector('[data-bs-product-related-counter]');

    var scrollLeft = track.scrollLeft;
    var maxScroll = track.scrollWidth - track.clientWidth;

    // Escrituras idempotentes (solo si cambia el valor): refreshNavUi corre dentro del callback
    // del MutationObserver del root; escribir en el DOM del subtree observado sin este guard
    // re-dispara el observer → bucle de reflows que clava el hilo en móvil.
    if (prevBtn) {
      var prevDisabled = scrollLeft < 1;
      if (prevBtn.disabled !== prevDisabled) {
        prevBtn.disabled = prevDisabled;
        prevBtn.setAttribute('aria-disabled', String(prevDisabled));
      }
    }

    if (nextBtn) {
      var nextDisabled = maxScroll <= 0 || scrollLeft >= maxScroll - 1;
      if (nextBtn.disabled !== nextDisabled) {
        nextBtn.disabled = nextDisabled;
        nextBtn.setAttribute('aria-disabled', String(nextDisabled));
      }
    }

    if (counter) {
      var totalCards = getCards(track).length;
      var step = getStep(track);
      var idx;
      if (maxScroll <= 0) {
        idx = 1;
      } else if (scrollLeft >= maxScroll - 2) {
        idx = totalCards;
      } else {
        idx = Math.round(scrollLeft / step) + 1;
      }
      idx = Math.max(1, Math.min(idx, totalCards || 1));
      var counterText = idx + ' / ' + totalCards;
      if (counter.textContent !== counterText) counter.textContent = counterText;
    }
  }

  /* Re-ata el listener de scroll al track ACTUAL (nodo nuevo tras cada innerHTML swap).
     Guard por nodo (data-attr) para no duplicar el listener si el MutationObserver dispara
     varias veces sobre el mismo track (ej: mutaciones anidadas del mismo render).
     rAF-throttle (INP): refreshNavUi lee offsetWidth/getComputedStyle (forced reflow) — sin
     coalescer, el momentum scroll táctil dispara 'scroll' decenas de veces por segundo y cada
     una fuerza layout síncrono. Se agrupa a máximo 1 recálculo por frame. */
  function bindTrack(root) {
    var track = root.querySelector('[data-bs-product-related-track]');
    if (!track) return;
    if (track.dataset.bsProductRelatedTrackInit) {
      refreshNavUi(root, track);
      return;
    }
    track.dataset.bsProductRelatedTrackInit = 'true';

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          refreshNavUi(root, track);
          ticking = false;
        });
      },
      { passive: true }
    );

    refreshNavUi(root, track);
  }

  function scrollByDirection(root, direction) {
    var track = root.querySelector('[data-bs-product-related-track]');
    if (!track) return;
    var step = getStep(track);
    if (!step) return;

    var maxScroll = track.scrollWidth - track.clientWidth;
    var target = direction === 'next' ? track.scrollLeft + step : track.scrollLeft - step;
    target = Math.max(0, Math.min(target, maxScroll));

    track.scrollTo({ left: target, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  /* ============================================================
     ADD-TO-CART — delegado, mismo patrón que bs-product-fbt.js (POST /cart/add.js +
     GET /cart.js + dispatch cart:update compatible con ThemeEvents de Horizon)
     ============================================================ */

  function showInlineError(card, message) {
    if (!card) return;
    var body = card.querySelector('.bs-product-related__body');
    if (!body) return;

    var errorEl = body.querySelector('.bs-product-related__form-error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'bs-product-related__form-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('aria-live', 'assertive');
      body.appendChild(errorEl);
    }
    errorEl.textContent = message;
    errorEl.classList.add('bs-product-related__form-error--visible');

    clearTimeout(errorEl._dismissTimer);
    errorEl._dismissTimer = setTimeout(function () {
      errorEl.classList.remove('bs-product-related__form-error--visible');
    }, 4500);
  }

  function dispatchCartUpdate(root, cart) {
    var detail = {
      resource: cart,
      sourceId: root.dataset.sectionId || 'bs-product-related',
      data: {
        source: 'bs-product-related',
        itemCount: cart ? cart.item_count : undefined,
        didError: false,
      },
    };
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: detail }));
  }

  function dispatchCartError(root, errorBody) {
    var detail = {
      sourceId: root.dataset.sectionId || 'bs-product-related',
      data: {
        message: errorBody.message,
        description: errorBody.description,
        errors: errorBody.errors,
      },
    };
    document.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: detail }));
  }

  async function handleAdd(root, btn) {
    if (btn.disabled) return;

    var variantId = btn.getAttribute('data-variant-id');
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
    if (!variantId) return;

    var card = btn.closest('.bs-product-related');
    var label = btn.querySelector('[data-bs-related-add-label]');
    var originalText = label ? label.textContent : '';

    btn.disabled = true;
    if (label) label.textContent = 'Añadiendo…';

    try {
      var addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      });

      if (!addResponse.ok) {
        var errorBody = await addResponse.json().catch(function () {
          return {};
        });
        dispatchCartError(root, errorBody);
        showInlineError(card, errorBody.description || errorBody.message || 'No se pudo añadir el producto.');
        btn.disabled = false;
        if (label) label.textContent = originalText;
        return;
      }

      var cart = null;
      try {
        var cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (e) {
        // sigue: el dispatch sin cart object igual activa el refresh del cart bubble
      }

      if (label) label.textContent = 'Añadido';
      dispatchCartUpdate(root, cart);

      setTimeout(function () {
        if (label) label.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error('[bs-product-related] add-to-cart failed:', error);
      showInlineError(card, 'Error de conexión. Inténtalo de nuevo.');
      btn.disabled = false;
      if (label) label.textContent = originalText;
    }
  }

  /* ============================================================
     INIT — delegación en el root (sobrevive al innerHTML swap) +
     MutationObserver para re-atar el track tras cada render.
     ============================================================ */

  function initRoot(root) {
    // R45: guard contra doble inicialización
    if (root.dataset.bsProductRelatedInit) return;
    root.dataset.bsProductRelatedInit = 'true';

    // R47: listener delegado en el root, no en document/window
    root.addEventListener('click', function (event) {
      var navBtn = event.target.closest('[data-direction]');
      if (navBtn) {
        scrollByDirection(root, navBtn.getAttribute('data-direction'));
        return;
      }

      var addBtn = event.target.closest('[data-bs-related-add]');
      if (addBtn) {
        handleAdd(root, addBtn);
      }
    });

    // Re-atar SOLO cuando product-recommendations inyecta un <track> nuevo (el swap real).
    // NO reaccionar a las mutaciones de texto que provoca refreshNavUi (contador) ni a las de
    // add-to-cart (labels/errores): si el observer se disparara con esas, se auto-alimentaría en
    // bucle y cada vuelta forzaría un reflow síncrono → cuelgue del hilo principal en móvil.
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        if (rec.type !== 'childList') continue;
        var added = rec.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (
            (n.matches && n.matches('[data-bs-product-related-track]')) ||
            (n.querySelector && n.querySelector('[data-bs-product-related-track]'))
          ) {
            bindTrack(root);
            return;
          }
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    bindTrack(root);
  }

  function init(scope) {
    // R44: querySelectorAll para todas las instancias
    var roots = (scope || document).querySelectorAll('.bs-product-related-root');
    for (var i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  init();

  // R48: reinit en el Customizer (el Customizer reemplaza el nodo entero de la section)
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
